import type React from 'react';
import { useCallback, useState, type FC, useMemo } from 'react';
import {
  UiButton,
  UiCheckbox,
  UiHStack,
  UiIconArrowsClockwise, UiIconCaretRight, UiIconCheckSquare, UiIconSquare,
  UiIconUser, UiIconUsersThree,
  UiStack,
  uiStyles,
  UiText,
} from '@/lib/ui';
import BaseTransitionSlideLeft from '@/base/Transition/SlideLeft';
import LayoutFooter from '@/registration/component/Register/Layout/Footer';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { useIsFetching, useMutation, useQueryClient } from '@tanstack/react-query';
import { type ApiResponseSingle } from '@/api/tenantClient';
import { registrationQueryKey, verifyUser, type VerifyUserRequest, type VerifyUserResponse } from '@/api/registration';
import { useTenantApi } from '@/account/hook/useTenantApi';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import { generatePageUrl } from '@/app/pages';
import BaseMessageBarError from '@/base/MessageBar/Error';
import BaseFormInputField from '@/base/Form/InputField';
import { useUserSession } from '@/registration/hook/useUserSession';
import { groupRegistrationQueryKey } from '@/api/registration/groupRegistration';
import { useGroupAttendeeCategoryQuery } from '@/registration/hook/useGroupAttendeeCategoryQuery';
import { EMAIL_REGEX } from '@/lib/util/regex';
import { useEventSettingsQuery } from '@/registration/hook/useEventSettingsQuery';
import BrandFooter from '@/registration/component/Register/Layout/BrandFooter';
import BaseThemeButton from '@/base/ThemeButton';
import { useKitchenSink } from '@/app/ProviderKitchenSink';

const formSchema = Yup.object({
  email: Yup.string().matches(EMAIL_REGEX, 'Invalid email format').required('Email is required')
});

interface EmailLayoutProps {
  onShowPasscode: (email: string) => void,
  groupRegistrationEnabled: boolean,
  toggleGroupRego: () => void,
  isGroupRego: boolean,
}

const EmailLayout: FC<EmailLayoutProps> = ({
  onShowPasscode,
  groupRegistrationEnabled,
  toggleGroupRego,
  isGroupRego,
}) => {
  const { userSession, setUserSession } = useUserSession();
  const { createTenantApiRequest } = useTenantApi();
  const [errors, setErrors] = useState<string[]>([]);
  const navigate = useNavigate();
  const { tenantCode, eventId } = useRegisterRoute();
  const { data: eventSettingData } = useEventSettingsQuery(eventId);
  const queryClient = useQueryClient();
  const isFetching = useIsFetching();

  const {attendeeCategoryId} = useKitchenSink();
  const attendeeCategoryQuery = useGroupAttendeeCategoryQuery(eventId);
  const isisSupportedGroup = useMemo(() => {
    return Array.isArray(attendeeCategoryQuery?.data) && attendeeCategoryQuery?.data?.some(item => {
      // if (attendeeCategoryId) {
      //   return item.isVisible || (item.id === Number(attendeeCategoryId) && item.groupRegistrationEnabled === 'yes');
      // } else {
      //   return item.groupRegistrationEnabled === 'yes' && item.isVisible;
      // }
      if (attendeeCategoryId) {
        // We don't care if the attendee category is visible if an explicit attendee category id is passed.
        return item.id === Number(attendeeCategoryId) && item.groupRegistrationEnabled === 'yes';
      } else {
        return item.isVisible && item.groupRegistrationEnabled === 'yes';
      }
    });
  }, [
    attendeeCategoryQuery?.data,
    attendeeCategoryId,
  ]);

  const toNextStep = useCallback(async () => {
    if (groupRegistrationEnabled) {
      navigate(generatePageUrl('RegistrationRegisterGroupCategory', { tenantCode, eventId }));
    } else {
      navigate(generatePageUrl('RegistrationRegisterLandingPage', { tenantCode, eventId }));
    }
  }, [eventId, groupRegistrationEnabled, navigate, tenantCode]);

  const { mutateAsync: verifyUserMutate, isLoading: isVerifying } = useMutation<ApiResponseSingle<VerifyUserResponse>, Error, VerifyUserRequest>({
    mutationFn: async (data: VerifyUserRequest) => { return await verifyUser(createTenantApiRequest)(data); },
    onSuccess: (data: ApiResponseSingle<VerifyUserResponse>) => {
      if (data.errors && data.errors.length > 0) {
        return setErrors(data.errors);
      }
    }
  });

  const onVerifyUser = useCallback(async (email: string) => {
    setUserSession(undefined);
    const data = await verifyUserMutate({ email, eventId });
    if (data.item.existingUser) {
      onShowPasscode(email);
    } else {
      const tokenResponse = data.item.token!;
      setUserSession({ email: tokenResponse.uid, client: tokenResponse.client, authToken: tokenResponse.token });
      void toNextStep();
    }
  }, [setUserSession, verifyUserMutate, eventId, onShowPasscode, toNextStep]);

  const onSubmit = useCallback(async (values: { email: string }) => {
    if (userSession?.email) {
      if (groupRegistrationEnabled) {
        await queryClient.invalidateQueries({ queryKey: [groupRegistrationQueryKey, { eventId }, { token: userSession?.authToken }] });
      } else {
        await queryClient.invalidateQueries({ queryKey: [registrationQueryKey, { eventId }, { token: userSession?.authToken }] });
      }
      void toNextStep();
    } else {
      await onVerifyUser(values.email);
    }
  }, [userSession?.email, userSession?.authToken, queryClient, eventId, toNextStep, onVerifyUser]);

  // const handleChangeRegistrationType = async (e: React.ChangeEvent<HTMLInputElement>) => {
  //   // await queryClient.invalidateQueries();
  //   toggleGroupRego(e.target.checked);
  // };

  const logOut = () => {
    setUserSession(undefined);
  };

  const groupCheckboxLabel = eventSettingData?.pageMetadata?.email?.groupCheckboxLabel || "I'm registering for a group";

  return (
    <Formik<{ email: string }>
      initialValues={{ email: userSession?.email ?? '' }}
      validateOnChange={false}
      validateOnBlur={false}
      validationSchema={formSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      <Form>
        <UiStack spacing={6}>
          <BaseTransitionSlideLeft key="email">
            {userSession?.email ? (
              <UiStack
                flexGrow={1}
                shadow={uiStyles.cardShadow}
                borderRadius={uiStyles.borderRadius}
              >
                <UiHStack
                  bgColor={'#fff'}
                  borderRadius={uiStyles.borderRadius}
                  spacing={0}
                  p={0}
                >
                  <UiStack
                    p={6}
                    display={{ base: 'none', lg: 'flex' }}
                    borderLeftRadius={uiStyles.borderRadius}
                    borderRightWidth={'1px'}
                    borderRightColor={'gray.200'}
                    borderRightStyle={'solid'}
                  >
                    <UiIconUser size={'2xl'} />
                  </UiStack>
                  <UiHStack p={6} spacing={4} flexGrow={1}>
                    <UiText
                      noOfLines={1}
                      style={{ wordBreak: 'break-word' }}
                      // color={'text.secondary'}
                      flexGrow={1}
                    >
                      {userSession?.email}
                    </UiText>
                    <UiStack onClick={logOut} {...uiStyles.hover}>
                      <UiIconArrowsClockwise color={'primary.500'} size={'2xl'} />
                    </UiStack>
                  </UiHStack>
                </UiHStack>
              </UiStack>
            ) : (
              <UiStack spacing={4} flexGrow={1} pt={8}>
                {errors.length > 0 && (
                  <UiStack spacing={4} flexGrow={1} py={4}>
                    {errors.map((error) => {
                      return (
                        <BaseMessageBarError key={error}>
                          {error}
                        </BaseMessageBarError>
                      );
                    })}
                  </UiStack>
                )}
                <BaseFormInputField
                  name={'email'}
                  label={'Please enter your email'}
                  isRequired={true}
                  layout={'stack'}
                />
              </UiStack>
            )}
          </BaseTransitionSlideLeft>
          {Array.isArray(attendeeCategoryQuery?.data) && attendeeCategoryQuery?.data?.length > 0 && isisSupportedGroup && (
            <UiHStack
              alignItems={'center'}
              bgColor={isGroupRego ? 'primary.50' : 'transparent'}
              borderWidth={'1px'}
              borderStyle={'solid'}
              borderColor={'gray.300'}
              borderRadius={uiStyles.borderRadius}
              // px={{base: 4, lg: 8}}
              // py={6}
              spacing={4}
              onClick={toggleGroupRego}
              {...uiStyles.hover}
            >
              <UiHStack
                borderRadius={uiStyles.borderRadius}
                spacing={0}
                p={0}
                justifyContent={'space-between'}
                flexGrow={1}
              >
                <UiStack
                  p={6}
                  display={{ base: 'none', lg: 'flex' }}
                  borderLeftRadius={uiStyles.borderRadius}
                  borderRightWidth={'1px'}
                  borderRightColor={'gray.200'}
                  borderRightStyle={'solid'}
                >
                  <UiIconUsersThree size={'2xl'} />
                </UiStack>
                <UiHStack p={6} spacing={4} flexGrow={1}>
                  <UiText
                    noOfLines={1}
                    style={{ wordBreak: 'break-word' }}
                    // color={'text.secondary'}
                    flexGrow={1}
                  >
                    {groupCheckboxLabel}
                  </UiText>
                  {isGroupRego ? (
                    <UiIconCheckSquare color={'primary.500'} />
                  ) : (
                    <UiIconSquare color={'primary.500'} />
                  )}
                </UiHStack>
              </UiHStack>
            </UiHStack>
          )}
          <UiStack height={48} />
          <LayoutFooter enableProgressBar={false}>
            <UiHStack justifyContent={'flex-end'} flexGrow={1}>
              <UiStack width={24} />
              <BrandFooter />
              {/*<UiButton*/}
              {/*  size={'lg'}*/}
              {/*  colorScheme={'primary'}*/}
              {/*  type={'submit'}*/}
              {/*  isLoading={isVerifying || isFetching > 0}*/}
              {/*  variant={'ghost'}*/}
              {/*  fontSize={'lg'}*/}
              {/*  rightIcon={(*/}
              {/*    <UiIconCaretRight color={'primary.800'} size={'xl'} weight={'bold'}/>*/}
              {/*  )}*/}
              {/*>*/}
              {/*  Next*/}
              {/*</UiButton>*/}
              <BaseThemeButton
                theme={'primary'}
                type={'submit'}
                isLoading={isVerifying || isFetching > 0}
                rightIcon={(
                  <UiIconCaretRight color={'primary.800'} size={'lg'} weight={'bold'} />
                )}
              >
                Next
              </BaseThemeButton>
            </UiHStack>
          </LayoutFooter>
        </UiStack>
      </Form>
    </Formik>
  );
};

export default EmailLayout;
